import type { ColumnsType } from 'antd/es/table';
import { Alert, Drawer } from 'antd';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import { ResponsiveTable } from '@src/components/Table';
import type { IWorkJourneyManagementAttentionItem } from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import { getRouteToWorkJourneyMonthMatrix } from '@src/packages/workJourney/constants';

import { getCoverPositionNavigation } from '../../../CoverPositionDrawers/getCoverPositionNavigation';
import {
  getActionLabelId,
  isPrimaryAction,
  WorkJourneyAttentionStatusBadge,
} from '../../../WorkJourneyAttentionSection/components/WorkJourneyAttentionStatusBadge';
import { getAttentionDescription } from '../../../../utils/getAttentionDescription';
import styles from './styles.module.less';

type WorkJourneyCalendarDayDetailDrawerProps = {
  open: boolean;
  date: string | null;
  items: IWorkJourneyManagementAttentionItem[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
};

const getDayDrawerPrimaryActionLabelId = (
  actionType: IWorkJourneyManagementAttentionItem['actionType'],
): string => {
  if (actionType === 'resolve') {
    return getActionLabelId(actionType);
  }
  return 'workJourney.management.calendar.dayDrawer.cover';
};

export const WorkJourneyCalendarDayDetailDrawer = ({
  open,
  date,
  items,
  loading,
  error,
  onClose,
}: WorkJourneyCalendarDayDetailDrawerProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();

  const title = useMemo(() => {
    if (!date) {
      return intl.formatMessage({ id: 'workJourney.management.calendar.dayDrawer.titleFallback' });
    }
    const formatted = DateUtil.toDateString(date, DateUtil.DEFAULT_DT_DATE_FORMAT) || date;
    return intl.formatMessage(
      { id: 'workJourney.management.calendar.dayDrawer.title' },
      { date: formatted },
    );
  }, [date, intl]);

  const handleCover = (record: IWorkJourneyManagementAttentionItem) => {
    const { path, state } = getCoverPositionNavigation(record);
    history.push(getFullUrl(path, url), state);
  };

  const handleSeeMore = (record: IWorkJourneyManagementAttentionItem) => {
    if (!date) return;
    const parsed = DateUtil.toDateOrNull(date) ?? new Date(`${date}T12:00:00`);
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    history.push(
      getFullUrl(
        getRouteToWorkJourneyMonthMatrix({
          workJourneyConfigId: record.workJourneyConfigId,
          year,
          month,
        }),
        url,
      ),
    );
  };

  const columns: ColumnsType<IWorkJourneyManagementAttentionItem> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.journey' }),
      key: 'journey',
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <span className={styles.journeyCell} title={record.workJourneyConfigName}>
          {record.workJourneyConfigName}
        </span>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.reason' }),
      key: 'reason',
      width: 140,
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <WorkJourneyAttentionStatusBadge reason={record.reason} />
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.description' }),
      key: 'description',
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <span className={styles.descriptionCell}>
          {getAttentionDescription(record, intl.formatMessage)}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 260,
      render: (record: IWorkJourneyManagementAttentionItem) => (
        <ActionButton.Container
          maxVisibleButtons={2}
          buttons={[
            <ActionButton
              key={`${record.id}-cover`}
              label={intl.formatMessage({
                id: getDayDrawerPrimaryActionLabelId(record.actionType),
              })}
              onClick={() => handleCover(record)}
              type={isPrimaryAction(record.actionType) ? 'primary' : 'default'}
              color={isPrimaryAction(record.actionType) ? 'primary' : 'secondary'}
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
              className={styles.actionButton}
              disabledScaling
            />,
            <ActionButton
              key={`${record.id}-see-more`}
              label={intl.formatMessage({
                id: 'workJourney.management.calendar.dayDrawer.seeMore',
              })}
              onClick={() => handleSeeMore(record)}
              type="primary"
              color="primary"
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.GET}
              className={styles.actionButton}
              disabledScaling
            />,
          ]}
        />
      ),
    },
  ];

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      onClose={onClose}
      width={720}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <BoxContainer loading={loading}>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer title={<Title.Header value={title} />} goBack={onClose} />
        </BoxContainer>

        <BoxContainer content>
          <h3 className={styles.sectionTitle}>
            {intl.formatMessage({ id: 'workJourney.management.cover.conflict' })}
          </h3>

          {error ? (
            <Alert type="error" showIcon message={error} className={styles.errorAlert} />
          ) : items.length === 0 && !loading ? (
            <p className={styles.emptyState}>
              {intl.formatMessage({
                id: 'workJourney.management.calendar.dayDrawer.empty',
              })}
            </p>
          ) : (
            <ResponsiveTable dataSource={items} columns={columns} loading={loading} />
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
